import { Typography, Box, Skeleton, useMediaQuery, Button, Stack, Alert, Link, Tooltip } from "@mui/material";
import { MessageItem, SchoolSwitch, useTitle, ApiQueries, StudentAvatar, useNotifications, DiaryItem, DaymapIconType, DaymapIcon, NoItems, useSettings, DaymapSkeleton, useAuth } from 'daymap-react';
import CheckListIcon from '@mui/icons-material/CheckList';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate, Link as RouterLink } from "react-router";
import styles from './styles.module.scss'
import { PageContainer } from "../../components/layout";
import { NotificationButton, Notifications } from "./components/notifications";
import Interviews from './components/interviews'
import dayjs from "dayjs";
import { Capacitor } from "@capacitor/core";
import { QuickActionButton } from "./components/quickactions";
import { useDiaryItemClick } from "../../components/diary";
function Home() {
    useTitle("Home");
    const { IsImpersonating } = useAuth();
    const {settings} = useSettings();
    const md = useMediaQuery((theme) => theme.breakpoints.up('md'));
    const { setAllRead } = useNotifications();

    return <PageContainer sx={{pt: 2}}>
        <Header />
        <div className={styles.main}>
            <div className={styles.feed}>
                <section>
                    <SchoolNotices />
                </section>
                <section>
                    <ComingUp />
                </section>
                <section>
                    <Interviews />
                </section>
            </div>
            {md && <div>
                <section>
                    <Box display="flex" justifyContent="space-between">
                        <Typography sx={{ p: 1 }} variant="h3">Timeline</Typography>
                        {IsImpersonating && settings?.impersonationHideInbox &&
                            <Tooltip title="Some notifications may be hidden while impersonating" arrow>
                                <span
                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                    tabIndex={0}
                                    aria-label="Some notifications may be hidden while impersonating"
                                >
                                    <WarningAmberIcon
                                        sx={{ color: theme => theme.palette.warning.main }}
                                        aria-hidden={true}
                                    />
                                </span>
                            </Tooltip>}
                        {!IsImpersonating && <Button size="small" onClick={setAllRead} startIcon={<CheckListIcon/>}>Mark all as read</Button>}
                    </Box>
                    <Notifications />
                </section>
            </div>}
        </div>
    </PageContainer>
}

function Header() {
    const md = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const { isPending, data } = new ApiQueries.contact.currentUser().ToQuery();
    const { settings } = useSettings();
    if (!settings) return;
    const quickActions = settings.quickActionList || [];

    return <header>
        <Stack>
            {!isPending && !data?.StudentAccess && <Alert color="warning" icon={<DaymapIcon iconType={DaymapIconType.Profile} />}>
                To have access to student assessment, attendance and other information you will first need to confirm your identity with the school
            </Alert>}
            <Box sx={{ display: 'flex', gap: 2, py: 2, maxWidth: '100vw' }}>
                <Box sx={{ flexGrow: '1', minWidth: '0' }}>
                    {isPending ?
                        <>
                            <Skeleton variant="text" />
                            <Skeleton variant="text" />
                        </>
                        :
                        <UserDetails person={data!} />
                    }
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'end', gap: 1 }}>
                    {isPending ?
                        <>
                            <Skeleton variant="circular" width={40} height={40} />
                            <Skeleton variant="circular" width={40} height={40} />
                        </>
                        :
                        <>
                            <StudentAvatars />
                            {md && <NotificationButton />}
                            {!md && quickActions.length > 0 && <QuickActionButton />}
                        </>
                    }
                </Box>
            </Box>
        </Stack>
    </header>
}

function UserDetails({person}:{person:ApiQueries.contact.Person}) {
    return <>
        <Typography style={{ marginBottom: '-6px', overflow: 'hidden', textOverflow: 'ellipsis' }} fontSize=".8rem" color="text.secondary">Welcome back,</Typography>
        <Typography fontSize="1.3rem" fontWeight="500" >{'DisplaynameSurname' in person ? person.DisplaynameSurname : ""}</Typography>
        <div style={{whiteSpace: 'nowrap' }}>
            <SchoolSwitch disabled={!Capacitor.isNativePlatform()}/>
        </div>
    </>
}

function StudentAvatars() {
    const md = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const { isPending, error, data } = new ApiQueries.contact.studentsForContact().ToQuery();

    if (error) {
        console.log('Error getting students for contact', error);
        return null;
    }

    if (isPending) {
        return <>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="circular" width={40} height={40} />
        </>
    }

    return <>
        {data?.map(student => {
            return <Link aria-label={student.DisplaynameSurname} color="textPrimary" underline="none" component={RouterLink} to={`/student/${student.ID}`} key={student.ID} sx={{ display: "flex", gap: 1, alignItems: "center", cursor: "pointer", backgroundColor: 'rgba(var(--mui-palette-text-primaryChannel) / .15)', borderRadius: 20 }}>
                {md && <Typography sx={{pl: 2}}>
                    {student.Displayname}
                </Typography>}
                <StudentAvatar key={student.ID} studentId={student.ID} />
            </Link>
        })}
    </>
}

function SchoolNotices() {
    const { isPending, data } = new ApiQueries.message.recentPosts().ToQuery();
    const navigate = useNavigate();

    if (isPending)
        return <DaymapSkeleton type="listitemswithheading" />

    return (
        <>
            <Box display="flex" justifyContent="space-between">
                <Typography sx={{ p: 1 }} variant="h3">School Notices</Typography>
                <Button size="small" onClick={() => navigate('/messaging')} startIcon={<DaymapIcon iconType={DaymapIconType.Message} />}>All messages</Button>
            </Box>
            <Box sx={{ display: 'flex', flexFlow: 'column', gap: 1 }}>
                {data?.map(m => <MessageItem key={m.MessageID}
                    date={m.SentOn}
                    messageType={m.MessageType}
                    subject={m.Subject}
                    description={<span dangerouslySetInnerHTML={{ __html: m.Text }}></span>}
                    read={m.Read}
                    sentFrom={m.SenderName}
                    onClick={() => navigate(`/message/${m.MessageID}`)}
                />)}
                {data?.length === 0 && <NoItems>No School Notices</NoItems>}
            </Box>
        </>
    )
}

const eventsFrom = dayjs().toDate();
const eventsTo = dayjs().add(7, 'day').toDate();
function ComingUp() {
    // get events for the next 7 days
    const { data, isPending } = new ApiQueries.calendars.DiaryItems({from: eventsFrom, to: eventsTo}).ToQuery();
    const navigate = useNavigate();
    const { handleItemClick, modalElement } = useDiaryItemClick();

    if (isPending)
        return <DaymapSkeleton type="listitemswithheading" />

    return (
        <>
            {modalElement}
            <Box display="flex" justifyContent="space-between">
                <Typography sx={{ p: 1 }} variant="h3">Coming Up</Typography>
                <Button size="small" onClick={() => navigate('/calendar')} startIcon={<DaymapIcon iconType={DaymapIconType.Calendar} />}>View calendar</Button>
            </Box>
            <Box sx={{ display: 'flex', flexFlow: 'column', gap: 1 }}>
                {data?.map(d => <DiaryItem key={`${d.Type}_${d.ID}`} {...d} onClick={handleItemClick} />)}
                {data?.length === 0 && <NoItems>No events in the next 7 days</NoItems>}
            </Box>
        </>
    )
}

export default Home